import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, ChevronRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { apiErrorMessage } from "../services/api";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (pw !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/connexion"), 2500);
    } catch (err) {
      setError(apiErrorMessage(err, "Le lien est invalide ou a expiré."));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-12" data-testid="reset-password-page">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-8">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
        <ChevronRight size={15} />
        <span className="text-slate-50 font-semibold">Réinitialiser le mot de passe</span>
      </div>

      <div className="max-w-md mx-auto bg-[#10131A] rounded-2xl border border-[#1E232D] p-6 sm:p-8 shadow-sm">
        {!token ? (
          <div data-testid="reset-invalid" className="text-center py-4">
            <AlertCircle size={44} className="mx-auto text-red-500 mb-3" />
            <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-2">Lien invalide</h1>
            <p className="text-slate-300">Ce lien de réinitialisation est incomplet ou invalide.</p>
            <Link to="/mot-de-passe-oublie" className="btn-orange inline-block rounded-lg px-6 py-3 font-bold mt-6">Demander un nouveau lien</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="text-center py-4">
            <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-3" />
            <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-2">Mot de passe modifié !</h1>
            <p className="text-slate-300">Vous allez être redirigé vers la page de connexion…</p>
            <Link to="/connexion" className="btn-orange inline-block rounded-lg px-6 py-3 font-bold mt-6">Se connecter</Link>
          </div>
        ) : (
          <>
            <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-1">Nouveau mot de passe</h1>
            <p className="text-slate-400 text-[14px] mb-6">Choisissez un nouveau mot de passe pour votre compte.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Nouveau mot de passe</label>
                <div className="flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F] transition-colors">
                  <Lock size={18} className="text-slate-500" />
                  <input data-testid="reset-password" type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className="flex-1 px-3 py-3 outline-none text-slate-50" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="text-slate-500 hover:text-white">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </div>
              <div>
                <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Confirmer le mot de passe</label>
                <div className="flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F] transition-colors">
                  <Lock size={18} className="text-slate-500" />
                  <input data-testid="reset-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="flex-1 px-3 py-3 outline-none text-slate-50" />
                </div>
              </div>
              {error && <div data-testid="reset-error" className="text-[14px] text-red-500">{error}</div>}
              <button data-testid="reset-submit" type="submit" disabled={loading} className="btn-orange w-full rounded-lg py-3.5 font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-70">
                {loading && <Loader2 className="animate-spin" size={18} />} Réinitialiser mon mot de passe
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
